import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import {
    Stack,
    Box,
    Typography,
    Divider,
    Skeleton,
    Drawer,
    IconButton,
    Tooltip
} from '@mui/material';
import Query from './../components/Query';
import PreviousQueries from './../components/PreviousQueries';
import { useGetConfiguration } from '../api/UtilApi';
import { FormattedMessage } from 'react-intl';
import LinearIndeterminate from '../components/LinearIndeterminate';
import LandingPageFeeds from '../components/LandingPageFeeds';
import SearchResult from '../components/SearchResult';
import { useGetSearches, usePostSearch } from '../api/ContentApi';
import SearchHistory from '../components/SearchHistory';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import InfoIcon from '../components/InfoIcon';
import RoutePaths from '../routes/RoutePaths';

const DEBUG = false;
const FEATURE_FRONT_PAGE_FEEDS = process.env.REACT_APP_FEATURE_FRONT_PAGE_FEEDS === "true"
const FEATURE_CHAT_HISTORY = process.env.REACT_APP_FEATURE_CHAT_HISTORY === "true"
const FEATURE_SEARCH_RESULT_CHAT = process.env.REACT_APP_FEATURE_SEARCH_RESULT_CHAT === "true"
const FEATURE_SINGLE_CONTENT_CHAT = process.env.REACT_APP_FEATURE_SINGLE_CONTENT_CHAT === "true"
const FEATURE_ANALYSIS_METADATA = process.env.REACT_APP_FEATURE_ANALYSIS_METADATA === "true"

const CHAT_HISTORY_WIDTH = 500;

const SearchPage = () => {
    const navigate = useNavigate();
    const [exampleState, setExampleState] = useState(0);
    const { search_id } = useParams();
    const [search, setSearch] = useState(null);
    const [queryInput, setQueryInput] = useState("");

    const configurationQuery = useGetConfiguration({});
    const searchesQuery = useGetSearches({});
    const searchQuery = useGetSearches({
        parameters: { id: search_id },
        enabled: !!(search_id)
    });

    useEffect(() => {
        //setSearch(null);
        //setQueryInput("");

        if (searchesQuery.isLoading || searchesQuery.isFetching) return;

        if (searchesQuery.data &&
            searchesQuery.data.result &&
            searchesQuery.data.result.length > 0) {
            const searchMatch = searchesQuery.data.result.find(search => search.id === search_id);

            if (searchMatch) {
                if (DEBUG) console.log("SearchPage :: Search found in searchesQuery: ", searchMatch);
                setSearch(searchMatch);
                setQueryInput(searchMatch.query);
            } else if (search_id && searchQuery.data && searchQuery.data.result.id === search_id) {
                // If search not found in searchesQuery, check searchQuery
                if (DEBUG) console.log("SearchPage :: Search found in searchQuery: ", searchQuery.data.result);
                setSearch(searchQuery.data.result);
                setQueryInput(searchQuery.data.result.query);
            } else {
                if (DEBUG) console.error("SearchPage :: Search not found: ", search_id);
                setSearch(null);
                setQueryInput("");
            }
        } else {
            if (DEBUG) console.error("SearchPage :: No searches found in response: ", searchesQuery.data);
            if (searchesQuery.isError) {
                console.error("SearchPage :: Error in searchesQuery: ", searchesQuery.error);
                setSearch(null);
                setQueryInput("");
            }
            // TODO navigate to safe
        }
    }, [
        search_id,
        searchesQuery.data,
        searchQuery.data]);

    const [isChatHistoryOpen, setIsChatHistoryOpen] = useState(false);

    const toggleDrawer = () => {
        setIsChatHistoryOpen((prev) => !prev);
    };

    const displaySearchHistory = FEATURE_CHAT_HISTORY &&
        searchesQuery.data &&
        searchesQuery.data.result &&
        searchesQuery.data.result.length > 0;


    const searchMutation = usePostSearch({
        onSuccess: (data) => {
            if (DEBUG) console.log("SearchPage :: searchMutation :: onSuccess ", data);
            navigate(`${RoutePaths.root.search}/${data.result.id}`);
        },
        onError: (error) => {
            console.error("SearchPage :: searchMutation :: onError ", error);
        }
    });

    if (configurationQuery.isError) {
        console.error("SearchPage :: Error loading configuration: ", configurationQuery.error);
        return (
            <Box mx="10%" pt={3} pb={4}>
                <Typography variant="body">
                    <FormattedMessage id="error.configuration" />
                </Typography>
            </Box>
        );
    }

    //if (searchQuery.isError) {
    //    console.error("SearchPage :: Error loading configuration: ", configurationQuery.error);
    //    return (
    //        <Box mx="10%" pt={3} pb={4}>
    //            <Typography variant="body">
    //                <FormattedMessage id="error.results" />
    //            </Typography>
    //        </Box>
    //    );
    //}

    return (
        <Box mx="10%" pt={3} pb={4}>
            {
                // Loading configuration
                configurationQuery.isLoading &&
                <Stack direction="column" spacing={2}>
                    <Typography variant="body">
                        <FormattedMessage id="landing.configuration.loading" />
                    </Typography>
                    <LinearIndeterminate />
                </Stack>
            }
            {
                // Error in loading configuration
                configurationQuery.isError &&
                <Typography variant="body">
                    <FormattedMessage id="landing.configuration.error" />
                </Typography>
            }
            {
                // Configuration loaded - show page
                configurationQuery.data && <Stack direction="column" spacing={6} align="start">
                    <Drawer
                        variant="persistent"
                        open={isChatHistoryOpen}
                        sx={{
                            width: isChatHistoryOpen ? CHAT_HISTORY_WIDTH : 0,
                            flexShrink: 0,
                            zIndex: 1800,
                            transition: 'width 0.7s ease',
                            '& .MuiDrawer-paper': {
                                width: isChatHistoryOpen ? CHAT_HISTORY_WIDTH : 0,
                                boxSizing: 'border-box',
                                transition: 'width 0.7s ease',
                            },
                        }}
                    >{displaySearchHistory &&
                        <Stack
                            direction="column"
                            spacing={2}
                            alignItems="flex-start"
                            align="start"
                            p={2}>
                            <Typography
                                variant="h5"
                                mt={2}
                                style={{
                                    textAlign: "left",
                                }}>
                                <FormattedMessage id="search.history.title" />
                            </Typography>
                            <Box
                                sx={{
                                    //flex: 1,
                                    //display: "flex",
                                    maxHeight: "100%",
                                    overflowY: "auto",
                                    width: "100%",
                                    //marginTop: "0px",
                                }}
                                mt={1}>
                                <SearchHistory
                                    searchesQuery={searchesQuery}
                                    clickAction={toggleDrawer} />
                            </Box>
                        </Stack>
                        }
                    </Drawer>
                    {
                        displaySearchHistory &&
                        <IconButton
                            onClick={toggleDrawer}
                            sx={{
                                position: 'fixed',
                                top: isChatHistoryOpen ? -35 : 25,
                                left: isChatHistoryOpen ? CHAT_HISTORY_WIDTH - 70 : 24,
                                zIndex: 1900,
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            }}
                        >
                            {isChatHistoryOpen ?
                                <MenuOpenIcon fontSize={"small"} /> :
                                <MenuIcon fontSize={"small"} />}
                        </IconButton>
                    }
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start", // key: prevents stretch
                            width: "100%",
                        }}
                    >
                        <Stack
                            direction="column"
                            spacing={5}
                            align="start"
                            width={
                                displaySearchHistory && false ?
                                    "63%" : "100%"
                            }
                            pr={4}>
                            <Typography variant="h4" style={{ textAlign: "left" }}>
                                <FormattedMessage id="query.title" /> <InfoIcon id="info.title" />
                            </Typography>
                            <Query
                                selectedSearch={search}
                                input={queryInput}
                                setInput={setQueryInput}
                                configuration={configurationQuery.data.result}
                                searchMutation={searchMutation} />
                        </Stack>
                    </Box>
                    {
                        !search && search_id && searchesQuery.isFetching &&
                        <Skeleton variant="rectangular" width="100%" height={400} />
                    }
                    {
                        (!search_id && FEATURE_FRONT_PAGE_FEEDS) &&
                        <LandingPageFeeds
                            configuration={configurationQuery.data.result}
                        />
                    }
                    {
                        searchQuery.isError &&
                        <Box>
                            <Divider />
                            <br />
                            <Typography variant="body">
                                <FormattedMessage id="error.results" />
                            </Typography>
                        </Box>
                    }
                    {
                        !!search && search.id && search.id === search_id && !searchMutation.isPending && !searchQuery.isError &&
                        <SearchResult
                            key={search.id}
                            {...{
                                search,
                                searchQuery,
                                configuration: configurationQuery.data.result
                            }} />
                    }
                </Stack>
            }
        </Box >
    );
}

export default SearchPage;